import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Moon, Sun, Sunrise, Sunset, Leaf, Droplets, Flame, Wind, Circle, AlertTriangle, Sparkles } from "lucide-react";
import dayjs from "dayjs";

const ELEMENT_META = {
  earth: { label: "Earth", icon: Leaf, color: "bg-forest text-bone", tint: "text-forest", tone: "Grounding, embodiment, nourishment, stability, land, food, structure, discipline, physical regeneration." },
  water: { label: "Water", icon: Droplets, color: "bg-terracotta text-bone", tint: "text-terracotta", tone: "Flow, emotion, purification, relationship, creativity, receptivity, renewal, adaptability." },
  fire: { label: "Fire", icon: Flame, color: "bg-terracotta text-bone", tint: "text-terracotta", tone: "Transformation, courage, vitality, will, leadership, purpose, creative ignition." },
  air: { label: "Air", icon: Wind, color: "bg-forest text-bone", tint: "text-forest", tone: "Breath, clarity, communication, perception, study, vision, expression, integration." },
  ether: { label: "Ether", icon: Circle, color: "bg-ink-soft text-bone", tint: "text-ink-soft", tone: "The integrating field present in every year." },
};

const LUNAR_LABEL = {
  new_moon: "New Moon", waxing_crescent: "Waxing Crescent", first_quarter: "First Quarter",
  waxing_gibbous: "Waxing Gibbous", full_moon: "Full Moon", waning_gibbous: "Waning Gibbous",
  last_quarter: "Last Quarter", waning_crescent: "Waning Crescent",
};
const BROAD_ACTIVITY = {
  new_moon: { label: "Meditation · Intention · Silence · Study", icon: Moon },
  waxing: { label: "Preparation · Learning · Building · Nourishment", icon: Sunrise },
  full_moon: { label: "Ceremony · Celebration · Music · Dance · Gathering", icon: Sun },
  waning: { label: "Integration · Release · Evaluation · Restoration", icon: Sunset },
};

const STATUS_STYLE = {
  concept: "bg-ink-soft/15 text-ink-soft",
  draft: "bg-forest/10 text-forest",
  practitioner_review: "bg-terracotta/15 text-terracotta",
  approved_curriculum: "bg-forest text-bone",
  scheduled: "bg-forest/70 text-bone",
  active: "bg-terracotta text-bone",
  completed: "bg-ink-soft/25 text-ink-soft",
  evaluated: "bg-ink-soft/25 text-ink-soft",
  revised: "bg-forest/10 text-forest",
};

export default function Curriculum() {
  const [ctx, setCtx] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [yearFilter, setYearFilter] = useState(1);

  useEffect(() => {
    api.get("/curriculum/context").then(({ data }) => setCtx(data)).catch(() => setCtx(null));
  }, []);
  useEffect(() => {
    api.get(`/curriculum/cycles`, { params: { year_index: yearFilter, scope: "lunar" } })
      .then(({ data }) => setCycles(data)).catch(() => setCycles([]));
  }, [yearFilter]);

  const currentMoonId = ctx?.current_cycle?.id;
  const healthyCount = useMemo(() => cycles.filter(c => c.contains_health_content).length, [cycles]);

  if (!ctx) return <div className="text-ink-soft text-sm">Loading curriculum context…</div>;

  const el = ctx.elemental?.current_element ? ELEMENT_META[ctx.elemental.current_element] : null;
  const broad = ctx.lunar?.broad_phase;
  const BroadIcon = BROAD_ACTIVITY[broad]?.icon || Moon;

  return (
    <div className="space-y-8">
      <div>
        <div className="overline">Executive · Living Curriculum</div>
        <h1 className="font-serif text-4xl mt-1">Living Curriculum &amp; Medicine Wheel</h1>
        <p className="text-ink-soft mt-2 max-w-3xl leading-relaxed">
          The temporal spine of Celestial Seed. Departments define the body of the institution; this gives it a pulse.
          Location-independent. The moon continues. The seasons continue. The elements continue.
        </p>
      </div>

      {/* Current context */}
      <section className="bg-forest text-bone rounded-xl overflow-hidden" data-testid="curr-now-panel">
        <div className="p-6 md:p-8 grid md:grid-cols-3 gap-6">
          <div className="flex flex-col gap-2">
            <div className="overline text-bone/60">Today · UTC</div>
            <div className="font-serif text-2xl">{dayjs(ctx.now).format("MMMM D, YYYY")}</div>
            <div className="text-bone/70 text-sm">{ctx.season ? `${ctx.season} (${ctx.hemisphere || "north"})` : ""}</div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="overline text-bone/60 flex items-center gap-1.5"><Moon className="h-3 w-3" /> Lunar Phase</div>
            <div className="font-serif text-2xl">{LUNAR_LABEL[ctx.lunar?.phase] || ctx.lunar?.phase}</div>
            <div className="text-bone/70 text-sm">
              {ctx.lunar?.illumination_pct}% illumination · day {ctx.lunar?.age_days} of {Math.round(ctx.lunar?.cycle_length_days || 29.5)}
            </div>
            <div className="flex items-center gap-1.5 text-bone/85 text-xs mt-1">
              <BroadIcon className="h-3.5 w-3.5" />
              {BROAD_ACTIVITY[broad]?.label || ""}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="overline text-bone/60">Elemental Year</div>
            {ctx.elemental?.before_anchor ? (
              <>
                <div className="font-serif text-2xl">Not yet activated</div>
                <div className="text-bone/70 text-sm">Anchor: {ctx.elemental?.anchor_date} · {ctx.elemental?.days_until_anchor?.toFixed(0)} days to go</div>
              </>
            ) : el ? (
              <>
                <div className="font-serif text-2xl flex items-center gap-2">
                  <el.icon className="h-6 w-6" strokeWidth={1.5} />
                  Year {ctx.elemental?.current_year_index} · {el.label}
                </div>
                <div className="text-bone/70 text-sm">Moon {ctx.elemental?.moon_of_year} of 12 · day {ctx.elemental?.day_of_year}</div>
                <div className="text-bone/85 text-xs mt-1 italic">{el.tone}</div>
              </>
            ) : null}
          </div>
        </div>
      </section>

      {/* Health-content guardrail banner */}
      {healthyCount > 0 && (
        <div className="border border-terracotta/30 bg-terracotta/5 rounded-lg p-4 flex gap-3 items-start" data-testid="health-guardrail">
          <AlertTriangle className="h-5 w-5 text-terracotta shrink-0 mt-0.5" />
          <div className="text-sm text-ink">
            <strong className="text-terracotta">Practitioner-Review Required</strong>
            <p className="text-ink-soft mt-1 leading-relaxed">
              {healthyCount} of the {cycles.length} cycles below reference health, herbal, or clinical education content
              (<code>contains_health_content: true</code>). These cannot be moved to <em>approved_curriculum</em> or higher
              without first passing through <em>practitioner_review</em>. NEOS presents these as educational framing only —
              never as medical claims. Herbal protocols, cleanses, fasts, and endocrine interventions require qualified
              professional oversight, informed consent, and contraindication review before any operationalization.
            </p>
          </div>
        </div>
      )}

      {/* Year selector */}
      <div className="flex flex-wrap gap-2">
        {[1, 2, 3, 4].map(y => {
          const elKey = ["earth", "water", "fire", "air"][y - 1];
          const em = ELEMENT_META[elKey];
          const Icon = em.icon;
          const isYear = yearFilter === y;
          return (
            <button key={y} onClick={() => setYearFilter(y)} data-testid={`year-btn-${y}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs uppercase tracking-widest border transition-colors
                ${isYear ? `${em.color} border-transparent` : "bg-bone text-ink-soft border-line hover:border-forest"}`}>
              <Icon className="h-3.5 w-3.5" strokeWidth={1.5} /> Year {y} · {em.label}
            </button>
          );
        })}
      </div>

      {/* Cycles list */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cycles.length === 0 && (
          <div className="col-span-full text-ink-soft text-sm p-8 text-center border border-dashed border-line rounded-xl">
            No cycles seeded for Year {yearFilter} yet. Year 1 (Earth) is the activated year. Others reserved.
          </div>
        )}
        {cycles.map(c => {
          const isCurrent = c.id === currentMoonId;
          return (
            <div key={c.id}
              data-testid={`cycle-${c.moon_index}`}
              className={`bg-admin-surface border rounded-xl p-5 flex flex-col gap-3 transition-colors ${isCurrent ? "border-terracotta ring-2 ring-terracotta/30" : "border-line"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft">Moon {c.moon_index} · {c.season}</div>
                  <div className="font-serif text-lg text-ink leading-tight mt-1">{c.title}</div>
                </div>
                {isCurrent && <Badge className="bg-terracotta text-bone text-[10px] uppercase tracking-widest shrink-0"><Sparkles className="h-3 w-3 mr-1" />Now</Badge>}
              </div>
              <div className="text-sm text-ink-soft italic leading-relaxed">{c.theme}</div>
              {c.suggested_practices?.length > 0 && (
                <div>
                  <div className="overline text-ink-soft mb-1">Suggested practices</div>
                  <ul className="text-xs text-ink space-y-1">
                    {c.suggested_practices.slice(0, 4).map((p, i) => <li key={`${c.id}-p-${i}`}>· {p}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap mt-auto pt-2 border-t border-line">
                <Badge className={STATUS_STYLE[c.status] || "bg-ink-soft/15 text-ink-soft"}>
                  {(c.status || "").replace(/_/g, " ")}
                </Badge>
                {c.contains_health_content && (
                  <Badge className="bg-terracotta/15 text-terracotta text-[10px] uppercase tracking-widest">
                    <AlertTriangle className="h-3 w-3 mr-1" /> Practitioner review
                  </Badge>
                )}
                {c.chakra_focus && (
                  <Badge className="bg-forest/10 text-forest text-[10px] uppercase tracking-widest">
                    {c.chakra_focus.replace(/_/g, " ")}
                  </Badge>
                )}
                {(c.departments || []).slice(0, 2).map(d => (
                  <span key={d} className="text-[10px] uppercase tracking-widest text-ink-soft">· {d}</span>
                ))}
              </div>
              {c.notes && (
                <div className="text-[10px] text-ink-soft italic leading-relaxed border-t border-line pt-2">{c.notes}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Foundational principle footer */}
      <section className="bg-bone/60 border border-line rounded-xl p-6 text-sm leading-relaxed text-ink-soft" data-testid="curr-principle">
        <div className="overline mb-2">Foundational principle</div>
        <blockquote className="italic text-ink">
          &ldquo;You cannot rush the body&rsquo;s remembering. The Earth took eons to form. Your body took generations.
          Give it the time it needs to unbind what has been held for lifetimes.&rdquo;
        </blockquote>
        <p className="mt-4">
          The departments define what the institution does. The Living Curriculum determines <em>when</em>, <em>how</em>,
          and in what sequence it comes alive. This is not a health protocol. It is a rhythm. Any health,
          herbal, or clinical content within a cycle remains restricted until qualified practitioners review it.
        </p>
      </section>
    </div>
  );
}
